import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { COMPANY_DATA } from "../../constant/CompanyData";
import Eyebrow from "../common/Eyebrow";

const PrivacyPolicy = () => {
  return (
    <div className="bg-slate-50 py-16 px-6 sm:px-12 md:px-24">
      <Helmet>
        <title>Privacy Policy - QuantaRock</title>
        <meta
          name="description"
          content="How QuantaRock Systems Pvt Ltd collects, uses, and protects your personal information."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://quantarock.in/legal/privacy" />
      </Helmet>

      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <Eyebrow>Legal</Eyebrow>
        </div>
        <h1 className="mt-3 text-center text-4xl font-bold tracking-tight text-[var(--website-primary-color)]">
          Privacy Policy
        </h1>

        <div className="mt-10 space-y-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-12">
          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              1. Introduction
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              At QuantaRock Systems Pvt Ltd, we are committed to protecting
              your privacy. This Privacy Policy outlines how we collect, use,
              and safeguard your personal information when you interact with
              our website and services. By using our services, you agree to
              the collection and use of your data in accordance with this
              policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              2. Information We Collect
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We collect personal information that you provide to us when you
              use our website, such as your name, email address, phone
              number, and any other information you provide through contact
              forms or other interactive elements on the site.
            </p>
            <p className="mt-2 leading-relaxed text-slate-600">
              Additionally, we may collect non-personally identifiable
              information, such as your browser type, IP address, and usage
              data, which helps us improve our website and services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              3. Use of Information
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              The information we collect is used to provide, maintain, and
              improve our services, as well as to communicate with you. We
              may use your contact information to send you updates,
              promotional materials, or important notices related to our
              services.
            </p>
            <p className="mt-2 leading-relaxed text-slate-600">
              We do not sell or rent your personal information to third
              parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              4. Third-Party Services
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We use a small number of third-party services to operate our
              website and respond to inquiries, who process data on our
              behalf under their own privacy and security terms:
            </p>
            <ul className="mt-3 list-inside list-disc space-y-1.5 text-slate-600">
              <li>
                <strong>EmailJS</strong> — delivers contact form submissions
                to our team by email.
              </li>
              <li>
                <strong>Google Sheets / Google Apps Script</strong> — stores
                a record of contact form submissions.
              </li>
              <li>
                <strong>Tawk.to</strong> — powers the live chat widget on
                our website.
              </li>
              <li>
                <strong>WhatsApp Business</strong> — used if you choose to
                contact us via the WhatsApp button.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              5. Data Security
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We implement reasonable security measures, including access
              controls, to protect your personal information. While we take
              precautions to protect your data, no method of transmission
              over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              6. Cookies
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              Our website uses cookies to enhance user experience and
              personalize content. See our{" "}
              <Link
                to="/legal/cookies"
                className="font-semibold text-[var(--website-secondary-color)]"
              >
                Cookie Policy
              </Link>{" "}
              for details on the cookies we use and how to manage your
              preferences.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              7. Your Rights
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              You have the right to access, update, and delete your personal
              information. If you would like to exercise these rights or have
              any concerns about how your information is used, please contact
              us at privacy@quantarock.in. We will respond to your request in
              accordance with applicable Indian data protection law,
              including the Digital Personal Data Protection Act, 2023.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              8. Changes to This Privacy Policy
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We may update this Privacy Policy from time to time. Any
              changes will be posted on this page, and we encourage you to
              review it periodically to stay informed about how we are
              protecting your information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              9. Contact Us
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              If you have any questions or concerns regarding this Privacy
              Policy or our data practices, please contact us at:
            </p>
            <p className="mt-2 leading-relaxed text-slate-600">
              <strong>Email:</strong> {COMPANY_DATA.mailID}
            </p>
            <p className="mt-1 leading-relaxed text-slate-600">
              <strong>Phone:</strong> {COMPANY_DATA.phoneNumber}
            </p>
            <p className="mt-1 leading-relaxed text-slate-600">
              <strong>Address:</strong> {COMPANY_DATA.address}
            </p>
          </section>
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          Copyright &copy; {new Date().getFullYear()} — All rights reserved
          by {COMPANY_DATA.companyName}
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
